import { useRef } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { ModalButtonWithForm } from 'components/common';
import { AchievementsIndividualForm } from '.';

import type { IndividualAchievement } from 'shared-types';
import type { IndividualAchievementFormValues } from './AchievementsIndividualForm';

interface Props {
  individualAchievement: Pick<
    IndividualAchievement,
    | 'attachments'
    | 'comments'
    | 'completionDate'
    | 'customAttributes'
    | 'expirationDate'
    | 'id'
    | 'startedAtDate'
    | 'title'
    | 'workload'
  >;
  isRequest?: boolean;
  updateData: () => void;
}

const AchievementsIndividualEdit = ({
  individualAchievement,
  isRequest = false,
  updateData
}: Props) => {
  const { dispatch } = useDispatch();

  const { messages, serverConfig } = useReduxState();

  const uploadFiles = useRef<Function>(null);

  const initialValues = {
    attachments: individualAchievement?.attachments || [],
    comments: individualAchievement?.comments || '',
    completionDate: individualAchievement
      ? Date.parse(individualAchievement.completionDate as string)
      : null,
    customAttributes: individualAchievement?.customAttributes || {},
    expirationDate: individualAchievement?.expirationDate
      ? Date.parse(individualAchievement.expirationDate as string)
      : null,
    startedAtDate: individualAchievement?.startedAtDate
      ? Date.parse(individualAchievement.startedAtDate as string)
      : null,
    title: individualAchievement?.title || '',
    workload: individualAchievement?.workload || 0
  } as IndividualAchievementFormValues;
  if (serverConfig.individualAchievementCustomAttributes?.length) {
    for (const attr of serverConfig.individualAchievementCustomAttributes) {
      if (individualAchievement?.customAttributes?.[attr.key] !== undefined) {
        let value = individualAchievement.customAttributes[attr.key];
        if (attr.type.includes('Array') && !Array.isArray(value)) {
          initialValues.customAttributes[attr.key] = [value as string];
        } else initialValues.customAttributes[attr.key] = value;
      } else if (attr.type === 'Number') {
        initialValues.customAttributes[attr.key] = attr.min;
      }
    }
  }

  const submit = async (values: IndividualAchievementFormValues) => {
    try {
      const attachments = await uploadFiles.current();

      await api.put(
        `/admin/achievements-individual/${individualAchievement.id}`,
        {
          attachments,
          comments: values.comments,
          completionDate: values.completionDate,
          customAttributes: values.customAttributes,
          expirationDate: values.expirationDate,
          startedAtDate: values.startedAtDate,
          title: values.title?.trim(),
          workload: values.workload
        }
      );

      updateData();

      dispatch(
        actions.addNotice({
          text: messages.commonChangesSavesSuccessfully,
          type: 'success'
        })
      );
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  return (
    <div>
      <ModalButtonWithForm
        buttonCategory="success"
        buttonIcon="edit"
        buttonIsDropdown={isRequest}
        buttonText={
          isRequest ? messages.accessRequestEdit : messages.commonUpdateDetails
        }
        initialValues={initialValues}
        modalActionText={messages.commonSaveChanges}
        modalTitle={messages.achievementsIndividualUpdate}
        modalWidth="wide"
        onSubmit={submit}
      >
        {() => (
          <AchievementsIndividualForm
            getUploadAttachmentsHandler={(fn) => (uploadFiles.current = fn)}
            visible={true}
          />
        )}
      </ModalButtonWithForm>
    </div>
  );
};

export default AchievementsIndividualEdit;
